// LIBRARIES
import { useT } from "@transifex/react";
import { PreviousUrlNavigationProps } from "p6m-p6u";
import queryString from "query-string";
import { useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import CloseButton from "../../../basic/closeButton/CloseButton";
import PDFConfiguration from "../../../complex/create/pdfConfiguration/PDFConfiguration";
import { useStudentPracticeContext } from "../../../context/StudentPracticeContext";
import { useTestCreationContext } from "../../../context/TestCreationContext";
import { SwitchItemPosition } from "../../../enums/positions";
import { PreviewView } from "../../../enums/views";
import useTriggerOnNavigation from "../../../hooks/useTriggerOnNavigation";
import { Context as ResponsiveContext } from "react-responsive";
import OnlineTest from "../../../complex/onlineTest/OnlineTest";
import DesignConstants from "../../../constants/DesignConstants";
import ContentWithHeader from "../../../wrapper/ContentWithHeader/ContentWithHeader";

import {
    MiddleContentWrapper,
    PreviewModeSwitchItem,
    PreviewModeSwitchText,
    PreviewModeSwitchWrapper,
    TestPreviewHeader,
    Wrapper,
} from "./StyledComponents";

export interface PDFConfigurationRef {
    handleClose: () => Promise<void>;
}

const TestPreview = () => {
    const history = useHistory();
    const pdfConfigRef = useRef<PDFConfigurationRef | null>(null);
    const { search, state } = useLocation<PreviousUrlNavigationProps>();

    const { refreshPreviewData } = useStudentPracticeContext();
    const { testContent, resetAppStatus, loadUserTests } = useTestCreationContext();

    useEffect(() => {
        // loads data from main context into preview / practice context
        refreshPreviewData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [testContent]);

    const urlParams = queryString.parse(search.replace("?", ""));
    const modeFromUrl = urlParams.mode as PreviewView;

    const handleCleanUp = () => {
        resetAppStatus();
        loadUserTests(true);
    };

    // The preview is opened from the overview, from a class page or from mid-creation. Leaving to the
    // overview always ends the flow; the class page only counts when the preview started there. A
    // return to /create/* means the author is still editing, so the creation context has to survive.
    const returnPath = state?.urlBeforePreview || "/assistent";
    // returnPath keeps any query string for the actual navigation below; the clean-up list is matched
    // against location.pathname, so a query there would stop it from ever matching.
    const returnPathname = returnPath.split("?")[0];
    const cleanUpPaths = returnPath.startsWith("/create") ? ["/assistent"] : ["/assistent", returnPathname];

    useTriggerOnNavigation(handleCleanUp, cleanUpPaths);

    const t = useT();
    const t_desktop = t("Desktop", { _tags: "CreateTest,TestPreview" });
    const t_mobile = t("Mobile", { _tags: "CreateTest,TestPreview" });
    const t_pdf = t("PDF", { _tags: "CreateTest,TestPreview" });
    const t_closePreview = t("close preview", { _tags: "CreateTest,TestPreview" });

    const navigateToPreviewMode = async (mode: PreviewView) => {
        if (modeFromUrl === PreviewView.PDF && pdfConfigRef.current) {
            await pdfConfigRef.current.handleClose();
        }
        return history.replace(`/create/preview?mode=${mode}`, state);
    };

    const renderPreviewContent = () => {
        switch (modeFromUrl) {
            case PreviewView.PDF:
                return <PDFConfiguration ref={pdfConfigRef} />;
            case PreviewView.MOBILE:
                return (
                    <div className="h-[640px] w-[320px] mx-auto bg-background-light overflow-hidden">
                        <ResponsiveContext.Provider value={{ width: 320 }}>
                            <OnlineTest />
                        </ResponsiveContext.Provider>
                    </div>
                );
            case PreviewView.DESKTOP:
            default:
                return (
                    <div className="h-[640px] w-full mx-auto bg-background-light overflow-hidden">
                        <OnlineTest />
                    </div>
                );
        }
    };

    return (
        <ContentWithHeader
            hasGrayBackground
            isFullWidth={modeFromUrl === PreviewView.PDF}
            id={"content-with-header"}
        >
            <Wrapper>
                <CloseButton
                    data-testid="button-close-preview"
                    onClick={async () => {
                        if (pdfConfigRef.current) {
                            await pdfConfigRef.current.handleClose();
                        }
                        history.replace(returnPath);
                    }}
                    hintText={t_closePreview}
                    style={{ paddingRight: modeFromUrl === PreviewView.PDF ? DesignConstants.SPACES.S : "" }}
                />
                <TestPreviewHeader>
                    <PreviewModeSwitchWrapper>
                        <PreviewModeSwitchItem
                            data-testid={`preview-mode-switch-${PreviewView.MOBILE}`}
                            onClick={() => navigateToPreviewMode(PreviewView.MOBILE)}
                            currentMode={modeFromUrl}
                            mode={PreviewView.MOBILE}
                            position={SwitchItemPosition.LEFT}
                        >
                            <PreviewModeSwitchText>{t_mobile}</PreviewModeSwitchText>
                        </PreviewModeSwitchItem>

                        <PreviewModeSwitchItem
                            data-testid={`preview-mode-switch-${PreviewView.DESKTOP}`}
                            onClick={() => navigateToPreviewMode(PreviewView.DESKTOP)}
                            currentMode={modeFromUrl}
                            mode={PreviewView.DESKTOP}
                            position={SwitchItemPosition.CENTER}
                        >
                            <PreviewModeSwitchText>{t_desktop}</PreviewModeSwitchText>
                        </PreviewModeSwitchItem>

                        <PreviewModeSwitchItem
                            data-testid={`preview-mode-switch-${PreviewView.PDF}`}
                            onClick={() => navigateToPreviewMode(PreviewView.PDF)}
                            currentMode={modeFromUrl}
                            mode={PreviewView.PDF}
                            position={SwitchItemPosition.RIGHT}
                        >
                            <PreviewModeSwitchText>{t_pdf}</PreviewModeSwitchText>
                        </PreviewModeSwitchItem>
                    </PreviewModeSwitchWrapper>
                </TestPreviewHeader>
                <MiddleContentWrapper isPDF={modeFromUrl === PreviewView.PDF}>
                    {renderPreviewContent()}
                </MiddleContentWrapper>
            </Wrapper>
        </ContentWithHeader>
    );
};

export default TestPreview;
